import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Trash2, ShieldAlert, Loader2, User, Mail, Globe, ExternalLink, LogOut, Type, Eye, Camera, Briefcase, Save, Target, Shield } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BottomNav from "@/components/BottomNav";
import { useAuth } from "@/context/AuthContext";
import { useAccessibility } from "@/context/AccessibilityContext";
import { useT } from "@/hooks/useT";
import { LANGUAGES } from "@/components/LanguagePersonaBar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { buildLabel } from "@/lib/buildVersion";
import ConsentManager from "@/components/ConsentManager";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

/**
 * Settings & Privacy — user control center.
 * GDPR-compliant "Delete My Account" wired to POST /api/user/delete
 * (server wipes Mongo + Supabase, clears cookie, invalidates session).
 */
export default function Settings() {
  const { user, logout } = useAuth();
  const { lang, setLang } = useT();
  const { dyslexia, focusMode, setDyslexia, setFocusMode } = useAccessibility();
  const [credits, setCredits] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  // B2 — Smart Persona form. Hydrated from /api/user/prefs; saved via PUT.
  const [prefs, setPrefs] = useState({
    age: "", domain: "", seniority: "", primary_goal: "", picture_url: null,
  });
  const [savingPersona, setSavingPersona] = useState(false);
  const [trustOpen, setTrustOpen] = useState(false);
  const fileRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/credits/balance")
      .then(({ data }) => setCredits(data?.credits ?? 0))
      .catch(() => {});
    api.get("/user/prefs")
      .then(({ data }) => setPrefs((p) => ({
        ...p,
        age: data?.age ?? "",
        domain: data?.domain ?? "",
        seniority: data?.seniority ?? "",
        primary_goal: data?.primary_goal ?? "",
        picture_url: data?.picture_url ?? null,
      })))
      .catch(() => {});
  }, []);

  const onPickPicture = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 500_000) {
      toast.error("Image must be under ~500KB. Try a smaller crop.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPrefs((p) => ({ ...p, picture_url: String(reader.result || "") }));
    reader.readAsDataURL(f);
  };

  const savePersona = async () => {
    setSavingPersona(true);
    try {
      await api.put("/user/prefs", {
        language: lang || "en",
        persona: "expert",
        age: prefs.age ? Number(prefs.age) : null,
        domain: prefs.domain || null,
        seniority: prefs.seniority || null,
        primary_goal: prefs.primary_goal || null,
        picture_url: prefs.picture_url || null,
      });
      toast.success("Profile saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingPersona(false);
    }
  };

  const canConfirm = typed.trim().toUpperCase() === "DELETE";

  const handleDelete = async () => {
    if (!canConfirm || busy) return;
    setBusy(true);
    try {
      await api.post("/user/delete");
      try {
        localStorage.removeItem("oak_session_token");
        localStorage.removeItem("oak_welcomed");
        localStorage.removeItem("oak_admin_stats");
      } catch {}
      toast.success("Your account and data have been permanently deleted.");
      setConfirmOpen(false);
      // Clear in-memory state then route to landing.
      logout?.();
      setTimeout(() => navigate("/login", { replace: true }), 200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deletion failed — please contact support.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen pb-20 md:pb-0" data-testid="settings-page">
      <Header credits={credits} onOpenAd={() => {}} />

      <main className="max-w-3xl mx-auto px-6 md:px-10 py-10 md:py-14 space-y-8">
        <div>
          <div className="label-eyebrow">Account</div>
          <h1 className="font-display text-4xl tracking-tight text-white mt-1">Settings &amp; Privacy</h1>
          <p className="text-white/55 mt-2 text-sm">
            Manage your profile, language, and data. All changes apply immediately.
          </p>
        </div>

        {/* Profile */}
        <section className="glass p-6 space-y-4" data-testid="settings-profile">
          <div className="flex items-center gap-4">
            <div className="relative">
              {prefs.picture_url ? (
                <img src={prefs.picture_url} alt="" className="h-14 w-14 rounded-full border border-white/10 object-cover" data-testid="settings-profile-picture" />
              ) : user?.picture ? (
                <img src={user.picture} alt="" className="h-14 w-14 rounded-full border border-white/10" />
              ) : (
                <div className="h-14 w-14 rounded-full bg-white/10 grid place-items-center text-lg font-display">
                  {(user?.name || "U").slice(0, 1).toUpperCase()}
                </div>
              )}
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="absolute -bottom-1 -right-1 h-7 w-7 grid place-items-center rounded-full border border-white/20 bg-[#0A192F] text-[#00FF9D] hover:bg-[#0A192F]/80 transition"
                title="Change profile photo"
                data-testid="settings-upload-picture"
              >
                <Camera size={12} />
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onPickPicture}
                data-testid="settings-picture-input"
              />
            </div>
            <div className="min-w-0">
              <div className="font-display text-xl tracking-tight text-white truncate">{user?.name || "—"}</div>
              <div className="text-white/55 text-sm truncate flex items-center gap-1.5">
                <Mail size={13} /> {user?.email || "—"}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            <Link
              to="/referrals"
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs border border-white/10 hover:border-white/25 text-white/75 hover:text-white transition"
              data-testid="settings-referrals-link"
            >
              <User size={13} /> Referrals &amp; Invites
            </Link>
            <button
              onClick={logout}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs border border-white/10 hover:border-rose-400/40 text-white/75 hover:text-rose-200 transition"
              data-testid="settings-signout"
            >
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </section>

        {/* B2 — Smart Persona. Drives AI tone + depth on every generated path. */}
        <section className="glass p-6 space-y-4" data-testid="settings-persona">
          <div>
            <div className="label-eyebrow flex items-center gap-2">
              <Briefcase size={12} className="text-[#ffb067]" /> Professional Persona
            </div>
            <h2 className="font-display text-lg tracking-tight text-white mt-1">Tune how Oak AI Campus teaches you</h2>
            <p className="text-white/55 text-sm mt-1">
              These signals personalise tone, jargon level, and case studies in every path.
              Skip any field you'd rather keep private.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1.5 text-xs text-white/70">
              <span className="uppercase tracking-wider text-[10px] text-white/45">Age</span>
              <input
                type="number"
                min={13}
                max={99}
                value={prefs.age ?? ""}
                onChange={(e) => setPrefs((p) => ({ ...p, age: e.target.value }))}
                placeholder="e.g. 28"
                className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#0044FF]/60"
                data-testid="settings-persona-age"
              />
            </label>

            <label className="flex flex-col gap-1.5 text-xs text-white/70">
              <span className="uppercase tracking-wider text-[10px] text-white/45">Domain</span>
              <input
                value={prefs.domain ?? ""}
                onChange={(e) => setPrefs((p) => ({ ...p, domain: e.target.value }))}
                placeholder="e.g. Software Engineering"
                className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#0044FF]/60"
                data-testid="settings-persona-domain"
              />
            </label>

            <label className="flex flex-col gap-1.5 text-xs text-white/70">
              <span className="uppercase tracking-wider text-[10px] text-white/45">Seniority</span>
              <select
                value={prefs.seniority ?? ""}
                onChange={(e) => setPrefs((p) => ({ ...p, seniority: e.target.value }))}
                className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#0044FF]/60"
                data-testid="settings-persona-seniority"
              >
                <option value="" className="bg-[#0B0E14]">— Select —</option>
                <option value="student" className="bg-[#0B0E14]">Student</option>
                <option value="junior" className="bg-[#0B0E14]">Junior (0-2 yrs)</option>
                <option value="mid" className="bg-[#0B0E14]">Mid (3-5 yrs)</option>
                <option value="senior" className="bg-[#0B0E14]">Senior (5-10 yrs)</option>
                <option value="lead" className="bg-[#0B0E14]">Lead / Manager (10+ yrs)</option>
                <option value="executive" className="bg-[#0B0E14]">Executive / Director</option>
              </select>
            </label>

            <label className="flex flex-col gap-1.5 text-xs text-white/70">
              <span className="uppercase tracking-wider text-[10px] text-white/45 inline-flex items-center gap-1">
                <Target size={10} /> Primary Goal
              </span>
              <select
                value={prefs.primary_goal ?? ""}
                onChange={(e) => setPrefs((p) => ({ ...p, primary_goal: e.target.value }))}
                className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#0044FF]/60"
                data-testid="settings-persona-primary-goal"
              >
                <option value="" className="bg-[#0B0E14]">— Select —</option>
                <option value="upskilling" className="bg-[#0B0E14]">Upskilling</option>
                <option value="career_pivot" className="bg-[#0B0E14]">Career Pivot</option>
                <option value="mastery" className="bg-[#0B0E14]">Mastery / Depth</option>
                <option value="exam_prep" className="bg-[#0B0E14]">Exam Prep</option>
                <option value="curiosity" className="bg-[#0B0E14]">Curiosity</option>
              </select>
            </label>
          </div>

          <button
            onClick={savePersona}
            disabled={savingPersona}
            className="btn-primary rounded-full px-4 py-2 text-sm font-display font-semibold inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="settings-persona-save"
          >
            {savingPersona ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {savingPersona ? "Saving…" : "Save persona"}
          </button>
        </section>

        {/* Language */}
        <section className="glass p-6 space-y-3" data-testid="settings-language">
          <div className="flex items-center gap-2">
            <Globe size={16} className="text-[#8aa8ff]" />
            <h2 className="font-display text-lg tracking-tight text-white">Interface Language</h2>
          </div>
          <p className="text-white/55 text-sm">
            Oak AI Campus generates lessons natively in your chosen language. Switching here applies site-wide.
          </p>
          <select
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/85 focus:outline-none focus:border-[#0044FF]/60 w-full sm:w-72"
            data-testid="settings-language-select"
          >
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code} className="bg-[#0B0E14]">{l.label}</option>
            ))}
          </select>
        </section>

        {/* Cognitive Bridge — Care Suite */}
        <section className="glass p-6 space-y-4" data-testid="settings-care-suite">
          <div>
            <div className="label-eyebrow">Cognitive Bridge</div>
            <h2 className="font-display text-lg tracking-tight text-white mt-1">Care &amp; Accessibility</h2>
            <p className="text-white/55 text-sm mt-1">
              Two toggles tuned for learners with dyslexia, ADHD, or sensory overload. Both persist
              across sessions and apply instantly, site-wide.
            </p>
          </div>

          <ToggleRow
            Icon={Type}
            accent="text-[#00FF9D]"
            title="OpenDyslexic Mode"
            description="Switches every page to the reader-friendly OpenDyslexic font with relaxed letter-spacing and line-height."
            on={dyslexia}
            setOn={setDyslexia}
            testId="settings-toggle-dyslexia"
          />
          <ToggleRow
            Icon={Eye}
            accent="text-[#8aa8ff]"
            title="Focus Mode"
            description="Hides the bottom nav, credit pill, ads, and language selector so only the lesson stays on screen."
            on={focusMode}
            setOn={setFocusMode}
            testId="settings-toggle-focus"
          />
        </section>

        {/* Legal links */}
        <section className="glass p-6 space-y-3" data-testid="settings-trust-center">
          <div className="flex items-center gap-2">
            <Shield size={16} className="text-[#9bffcc]" />
            <h2 className="font-display text-lg tracking-tight text-white">Data Trust Center</h2>
          </div>
          <p className="text-white/60 text-sm leading-relaxed">
            Toggle which surfaces of Oak AI Campus you want to use. Disable AI processing, opt out
            of analytics, or change your data-residency region. Your choice is saved on this device
            and synced to your account.
          </p>
          <button
            onClick={() => setTrustOpen(true)}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-display font-semibold border border-[#00FF9D]/40 bg-[#00FF9D]/[0.08] text-[#9bffcc] hover:bg-[#00FF9D]/[0.18] transition"
            data-testid="settings-open-trust-center"
          >
            <Shield size={14} /> Open Trust Center
          </button>
        </section>

        <section className="glass p-6 space-y-2" data-testid="settings-legal">
          <h2 className="font-display text-lg tracking-tight text-white">Legal &amp; Compliance</h2>
          <div className="flex flex-wrap gap-4 pt-1 text-sm">
            <Link to="/privacy" className="text-[#8aa8ff] hover:text-white inline-flex items-center gap-1" data-testid="settings-link-privacy">
              Privacy Policy <ExternalLink size={12} />
            </Link>
            <Link to="/terms" className="text-[#8aa8ff] hover:text-white inline-flex items-center gap-1" data-testid="settings-link-terms">
              Terms of Service <ExternalLink size={12} />
            </Link>
            <Link to="/refund" className="text-[#8aa8ff] hover:text-white inline-flex items-center gap-1" data-testid="settings-link-refund">
              Refund Policy <ExternalLink size={12} />
            </Link>
          </div>
        </section>

        {/* Danger zone — GDPR */}
        <section
          className="rounded-2xl p-6 border border-rose-500/30 bg-rose-500/[0.04] space-y-3"
          data-testid="settings-danger-zone"
        >
          <div className="flex items-center gap-2">
            <ShieldAlert size={16} className="text-rose-300" />
            <h2 className="font-display text-lg tracking-tight text-white">Delete My Account</h2>
          </div>
          <p className="text-white/65 text-sm leading-relaxed">
            Permanently erase your Oak AI Campus account, learning paths, generated modules, exam history,
            credits, and payment records. This action is <strong>irreversible</strong> and complies
            with GDPR Article 17 (Right to Erasure).
          </p>
          <button
            onClick={() => { setTyped(""); setConfirmOpen(true); }}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-display font-semibold border border-rose-400/50 bg-rose-500/10 hover:bg-rose-500/20 text-rose-100 transition"
            data-testid="settings-delete-account"
          >
            <Trash2 size={14} /> Delete my account &amp; data
          </button>
        </section>
      </main>

      <Footer />
      <BottomNav />
      {trustOpen && (
        <ConsentManager open onClose={() => setTrustOpen(false)} />
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="glass border-rose-400/30 max-w-md" data-testid="delete-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display tracking-tight">
              <ShieldAlert size={18} className="text-rose-300" />
              Are you sure?
            </DialogTitle>
            <DialogDescription className="text-white/60">
              This will permanently delete <strong className="text-white">{user?.email}</strong> and
              all associated data. It cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 pt-2">
            <label className="text-xs text-white/55">
              Type <span className="font-mono-oak text-rose-200">DELETE</span> to confirm:
            </label>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-rose-400/60"
              placeholder="DELETE"
              data-testid="delete-confirm-input"
              autoFocus
            />
          </div>

          <DialogFooter className="gap-2">
            <button
              onClick={() => setConfirmOpen(false)}
              className="rounded-full px-4 py-2 text-sm border border-white/15 hover:border-white/30 text-white/75 hover:text-white transition"
              data-testid="delete-cancel"
              disabled={busy}
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={!canConfirm || busy}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-display font-semibold bg-rose-500/90 hover:bg-rose-500 text-white transition disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="delete-confirm"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              {busy ? "Deleting…" : "Delete forever"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ToggleRow({ Icon, accent = "text-white", title, description, on, setOn, testId }) {
  return (
    <div
      className={`flex items-start gap-4 rounded-xl border p-4 transition ${
        on ? "border-[#00FF9D]/30 bg-[#00FF9D]/[0.04]" : "border-white/10 bg-white/[0.02]"
      }`}
      data-testid={testId}
    >
      <div className={`mt-0.5 h-9 w-9 rounded-full bg-white/5 grid place-items-center ${accent}`}>
        <Icon size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-[15px] tracking-tight text-white">{title}</div>
        <p className="text-[13px] text-white/60 mt-0.5 leading-relaxed">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        onClick={() => setOn(!on)}
        className={`relative h-6 w-11 shrink-0 rounded-full transition border ${
          on
            ? "bg-[#00FF9D]/80 border-[#00FF9D]"
            : "bg-white/10 border-white/15 hover:bg-white/15"
        }`}
        data-testid={`${testId}-switch`}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
            on ? "left-5" : "left-0.5"
          }`}
        />
      </button>
    </div>
  );
}

